// Home — the operator's landing dashboard. It answers, at a glance: is anything
// broken, and is my automation fleet healthy? Layout, top to bottom:
//   0. Pulsars        — status strip + 7-day check-ins / proposals / actions (PulsarsReport.tsx;
//                       only while the region has Pulsars on)
//   1. KPI strip      — 7-day run volume, success rate, active deployments, runners online
//   2. Run outcomes   — succeeded vs. failed per day (lightweight CSS bars, no chart lib*)
//      AI usage        — credits used / included this period (from /api/usage/current)
//   3. Needs attention — recent failed / timed-out / running runs, failing step named
//      Live activity   — the SignalR step-event feed, seeded with recent run history
//
// *Home is eager (first authed paint, see routes.tsx), so it must not drag Kendo
//  Charts + hammerjs into the entry bundle the way the lazy Usage page can. The
//  7-bar outcome chart is hand-rolled from divs to keep the landing bundle small.
//
// One /api/dashboard/summary call backs tiles 1–3; AI usage and the live feed are
// sourced separately so a slow/empty one never blanks the rest.

import { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Button } from '@progress/kendo-react-buttons';
import { Notification, NotificationGroup } from '@progress/kendo-react-notification';
import { Fade } from '@progress/kendo-react-animation';
import type {
  CurrentUsage,
  DashboardRunnerTypeStats,
  DashboardSummary,
  PlaybookSummary,
  RunnerType,
  StandingReport,
  WorkflowActivityEvent,
  WorkflowRunListItem,
} from '@shared/api-types';
import { PageHeader } from '../../components/PageHeader';
import { RunnerOnboarding } from './RunnerOnboarding';
import { PulsarsReport } from './PulsarsReport';
import { Card, CardHeader, Kpi, SectionHeading } from './tiles';
import { FAILED_HATCH, KPI_GRID, WEEKDAY } from './tileStyles';
import { RunStatusBadge } from '../../components/RunStatusBadge';
import { useActivity } from '../../lib/activity';
import { useAuth } from '../../lib/auth';
import { statusColor, statusLabel } from '../../lib/runStatus';
import { getDashboardSummary, getRecentRuns } from '../../lib/dashboardApi';
import { listPlaybooks } from '../../lib/playbookApi';
import { getReport as getPulsarReport } from '../../lib/standingAgentApi';
import { getCurrentUsage } from '../../lib/settingsApi';
import { formatTime, parseApiDate } from '@shared/datetime';

// Respect the OS "reduce motion" setting for the one animated element here (the
// toast). SSR-safe and cheap; read once at render.
function prefersReducedMotion(): boolean {
  return typeof window !== 'undefined' && window.matchMedia
    ? window.matchMedia('(prefers-reduced-motion: reduce)').matches
    : false;
}

// ── shared bits ────────────────────────────────────────────────────────────

/** Compact "time ago" from an ISO timestamp — "now", "12m", "3h", "2d". */
function ago(iso: string | null | undefined): string {
  if (!iso) return '—';
  const then = parseApiDate(iso).getTime();
  if (Number.isNaN(then)) return '—';
  const secs = Math.max(0, Math.round((Date.now() - then) / 1000));
  if (secs < 60) return 'now';
  const mins = Math.round(secs / 60);
  if (mins < 60) return `${mins}m`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h`;
  return `${Math.round(hrs / 24)}d`;
}

// ── KPI strip ──────────────────────────────────────────────────────────────

function KpiStrip({ summary }: { summary: DashboardSummary | null }) {
  const pct = summary ? `${(summary.successRate7d * 100).toFixed(1)}%` : '—';
  const runnerSub =
    summary && (summary.runnersStale > 0 || summary.runnersOutdated > 0)
      ? [summary.runnersStale > 0 ? `${summary.runnersStale} stale` : null,
         summary.runnersOutdated > 0 ? `${summary.runnersOutdated} outdated` : null]
          .filter(Boolean).join(' · ')
      : 'all current';
  const runnerColor = summary && (summary.runnersStale > 0 || summary.runnersOutdated > 0)
    ? 'var(--warn)' : 'var(--fg-muted)';

  return (
    <div style={KPI_GRID}>
      <Kpi label="Runs (7d)" value={summary ? summary.runs7d.toLocaleString() : '—'} to="/runs" />
      <Kpi
        label="Success rate"
        value={pct}
        sub={summary ? `${summary.failed7d} failed` : undefined}
        subColor={summary && summary.failed7d > 0 ? 'var(--danger)' : 'var(--fg-muted)'}
        to="/runs"
      />
      <Kpi
        label="Active deployments"
        value={summary ? String(summary.activeDeployments) : '—'}
        sub={summary ? `across ${summary.environmentCount} env${summary.environmentCount === 1 ? '' : 's'}` : undefined}
        to="/deployments"
      />
      <Kpi
        label="Runners online"
        value={summary ? `${summary.runnersOnline}/${summary.runnersTotal}` : '—'}
        sub={summary ? runnerSub : undefined}
        subColor={runnerColor}
        to="/runners"
      />
    </div>
  );
}

// ── playbooks strip ──────────────────────────────────────────────────────────

function PlaybooksStrip({ playbooks }: { playbooks: PlaybookSummary[] | null }) {
  const count = (s: PlaybookSummary['liveStatus']) => playbooks?.filter((p) => p.liveStatus === s).length ?? 0;
  const active = count('Active');
  const waiting = count('Waiting');
  const needsApproval = count('NeedsApproval');
  const drafts = count('Draft');
  const total = playbooks?.length ?? 0;
  const val = (n: number) => (playbooks ? n.toLocaleString() : '—');

  return (
    <div style={KPI_GRID}>
      <Kpi label="Active" value={val(active)} to="/playbooks" />
      <Kpi label="Waiting" value={val(waiting)} to="/playbooks" />
      <Kpi
        label="Needs approval"
        value={val(needsApproval)}
        subColor={needsApproval > 0 ? 'var(--warn)' : 'var(--fg-muted)'}
        sub={playbooks ? (needsApproval > 0 ? 'awaiting a decision' : 'none pending') : undefined}
        to="/playbooks"
      />
      <Kpi
        label="Total playbooks"
        value={val(total)}
        sub={playbooks ? `${drafts} draft${drafts === 1 ? '' : 's'}` : undefined}
        to="/playbooks"
      />
    </div>
  );
}

// ── runs by runner type ──────────────────────────────────────────────────────

// Display metadata per runner flavor: the short mono badge + the human label and
// one-line kind shown on each card. Keyed by the DashboardRunnerTypeStats.runnerType
// enum name; the render order below is the design's row order (C# · PS · Py · AI).
const RUNNER_TYPE_META: Record<RunnerType, { name: string; mono: string; kind: string }> = {
  CSharp: { name: 'C#', mono: 'C#', kind: 'Compiled runner' },
  PowerShell: { name: 'PowerShell', mono: 'PS', kind: 'Script runner' },
  Python: { name: 'Python', mono: 'Py', kind: 'Script runner' },
  Agent: { name: 'Agent', mono: 'AI', kind: 'AI agent runner' },
};
const RUNNER_TYPE_ORDER: RunnerType[] = ['CSharp', 'PowerShell', 'Python', 'Agent'];

/** Average duration in seconds → "m:ss"; em-dash when nothing completed. */
function fmtAvgDuration(seconds: number | null): string {
  if (seconds == null) return '—';
  const total = Math.round(seconds);
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`;
}

function RunnerTypeStat({ label, value, color, dot }: { label: string; value: string; color: string; dot?: boolean }) {
  return (
    <div style={{ flex: 1, minWidth: 0 }}>
      <div style={{ fontSize: 10, letterSpacing: '.06em', textTransform: 'uppercase', color: 'var(--fg-dim)', fontWeight: 600, marginBottom: 5 }}>
        {label}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        {dot && <span aria-hidden style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--accent)', flex: '0 0 auto' }} />}
        <span style={{ fontFamily: 'var(--font-mono)', fontSize: 15, fontWeight: 600, color }}>{value}</span>
      </div>
    </div>
  );
}

function RunnerTypeCard({ stats }: { stats: DashboardRunnerTypeStats }) {
  const meta = RUNNER_TYPE_META[stats.runnerType];
  const decided = stats.succeeded + stats.failed;
  const successPct = decided === 0 ? '—' : `${(stats.successRate * 100).toFixed(1)}%`;

  return (
    <div
      style={{
        position: 'relative',
        background: 'var(--bg-elev)',
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius-lg)',
        padding: 18,
        overflow: 'hidden',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <div
          aria-hidden
          style={{
            width: 40, height: 40, flex: 'none', borderRadius: 'var(--radius-lg)',
            background: 'var(--bg-active)', border: '1px solid var(--border-str)',
            display: 'grid', placeItems: 'center',
            fontFamily: 'var(--font-mono)', fontSize: 14, fontWeight: 700, color: 'var(--accent)',
          }}
        >
          {meta.mono}
        </div>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--fg)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {meta.name}
          </div>
          <div style={{ fontSize: 11, color: 'var(--fg-dim)', marginTop: 2 }}>{meta.kind}</div>
        </div>
      </div>

      <div style={{ marginTop: 18 }}>
        <div style={{ fontFamily: 'var(--font-mono)', fontSize: 30, fontWeight: 700, letterSpacing: '-.02em', lineHeight: 1, color: 'var(--fg)' }}>
          {stats.total.toLocaleString()}
        </div>
        <div style={{ fontSize: 11, color: 'var(--fg-dim)', marginTop: 5 }}>total runs</div>
      </div>

      <div style={{ display: 'flex', gap: 16, marginTop: 16, paddingTop: 14, borderTop: '1px solid var(--border)' }}>
        <RunnerTypeStat label="Success" value={successPct} color="var(--success)" />
        <RunnerTypeStat label="Failures" value={stats.failed.toLocaleString()} color="var(--danger)" />
        <RunnerTypeStat label="Running" value={stats.running.toLocaleString()} color="var(--fg)" dot={stats.running > 0} />
        <RunnerTypeStat label="Avg" value={fmtAvgDuration(stats.avgDurationSeconds)} color="var(--fg-muted)" />
      </div>
    </div>
  );
}

function RunnerTypeCards({ summary }: { summary: DashboardSummary | null }) {
  // Always render four cards in the design's order. While loading (summary null)
  // or if the API omits a flavor, fall back to a zeroed card so the row is stable.
  const byType = new Map((summary?.runnerTypes ?? []).map((t) => [t.runnerType, t]));
  const cards = RUNNER_TYPE_ORDER.map(
    (type): DashboardRunnerTypeStats =>
      byType.get(type) ?? { runnerType: type, total: 0, succeeded: 0, failed: 0, running: 0, successRate: 0, avgDurationSeconds: null },
  );

  return (
    <section aria-label="Runners" style={{ marginBottom: 12 }}>
      <SectionHeading>Runners</SectionHeading>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 16 }}>
        {cards.map((t) => (
          <RunnerTypeCard key={t.runnerType} stats={t} />
        ))}
      </div>
    </section>
  );
}

// ── run-outcome bars ─────────────────────────────────────────────────────────

function RunOutcomesTile({ summary }: { summary: DashboardSummary | null }) {
  // Derive each bar's weekday label once (UTC — the bucket date is a UTC day),
  // then render the bars and the axis labels from the same rows.
  const days = (summary?.dailyOutcomes ?? []).map((d) => {
    const parsed = parseApiDate(d.date);
    return { ...d, label: WEEKDAY[Number.isNaN(parsed.getTime()) ? 0 : parsed.getUTCDay()] };
  });
  const max = Math.max(1, ...days.map((d) => d.succeeded + d.failed));
  const succeededColor = statusColor('succeeded');
  const failedColor = statusColor('failed');

  return (
    <Card style={{ display: 'flex', flexDirection: 'column' }}>
      <CardHeader title="Run outcomes · 7 days" link={{ to: '/runs', label: 'History' }} />
      {days.length === 0 ? (
        <p style={{ color: 'var(--fg-muted)', fontSize: 13, margin: 0 }}>No runs in the last 7 days.</p>
      ) : (
        <>
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: 6, height: 110 }}>
            {days.map((d) => {
              const total = d.succeeded + d.failed;
              return (
                <div
                  key={d.date}
                  // role=img + aria-label so screen readers get the counts without
                  // a hover; the title serves the same for a mouse.
                  role="img"
                  aria-label={`${d.label}: ${d.succeeded} succeeded, ${d.failed} failed`}
                  title={`${d.label}: ${d.succeeded} succeeded, ${d.failed} failed`}
                  style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', height: '100%', gap: 2 }}
                >
                  {/* Always-visible total: the value is readable without hovering. */}
                  <span aria-hidden style={{ textAlign: 'center', fontSize: 10, lineHeight: 1, color: 'var(--fg-muted)', marginBottom: 1, minHeight: 11 }}>
                    {total > 0 ? total : ''}
                  </span>
                  {d.failed > 0 && (
                    <div
                      style={{
                        height: `${(d.failed / max) * 100}%`,
                        background: failedColor,
                        // Diagonal hatch so the failed segment is distinguishable
                        // from succeeded by texture, not colour alone (WCAG AA).
                        backgroundImage: FAILED_HATCH,
                        borderRadius: '3px 3px 0 0',
                      }}
                    />
                  )}
                  {d.succeeded > 0 && (
                    <div style={{ height: `${(d.succeeded / max) * 100}%`, background: succeededColor, borderRadius: total === d.succeeded ? '3px 3px 0 0' : 0 }} />
                  )}
                  {total === 0 && <div style={{ height: 2, background: 'var(--border)' }} />}
                </div>
              );
            })}
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 6 }}>
            {days.map((d) => (
              <span key={d.date} style={{ flex: 1, textAlign: 'center', fontSize: 11, color: 'var(--fg-muted)' }}>{d.label}</span>
            ))}
          </div>
          <div style={{ display: 'flex', gap: 16, marginTop: 10, fontSize: 12, color: 'var(--fg-muted)' }}>
            <span>
              <span aria-hidden style={{ display: 'inline-block', width: 9, height: 9, borderRadius: 2, background: succeededColor, marginRight: 5, verticalAlign: 'middle' }} />
              Succeeded
            </span>
            <span>
              <span
                aria-hidden
                style={{
                  display: 'inline-block',
                  width: 9,
                  height: 9,
                  borderRadius: 2,
                  background: failedColor,
                  backgroundImage: FAILED_HATCH,
                  marginRight: 5,
                  verticalAlign: 'middle',
                }}
              />
              Failed / timed out
            </span>
          </div>
        </>
      )}
    </Card>
  );
}

// ── Usage ────────────────────────────────────────────────────────────────────

// Compact edition + limits caption, e.g. "Professional · 1 runner · 3
// instances/runner" (null maxRunners → "Unlimited runners").
function editionSummary(u: CurrentUsage): string {
  const runners = u.maxRunners === null
    ? 'Unlimited runners'
    : `${u.maxRunners} runner${u.maxRunners === 1 ? '' : 's'}`;
  return `${u.edition} · ${runners} · ${u.maxInstancesPerRunner} instances/runner`;
}

function AiUsageTile({ usage }: { usage: CurrentUsage | null }) {
  const used = usage?.usedCredits ?? 0;
  const included = usage?.includedCredits ?? 0;
  const overage = usage?.overageCredits ?? 0;
  // With no allotment, leave the bar empty rather than implying 100% used —
  // the caption below already says "No included allotment set".
  const pct = included > 0 ? Math.min(100, Math.round((used / included) * 100)) : 0;
  const headroom = included > 0 ? Math.max(0, 100 - Math.round((used / included) * 100)) : null;
  const topModels = (usage?.byModel ?? []).slice(0, 3);

  return (
    <Card style={{ display: 'flex', flexDirection: 'column' }}>
      <CardHeader title="Usage" link={{ to: '/settings/usage', label: 'Details' }} />
      {usage && (
        <div style={{ fontSize: 12, color: 'var(--fg-muted)', marginBottom: 10 }}>
          {editionSummary(usage)}
        </div>
      )}
      <div style={{ fontSize: 22, fontWeight: 600 }}>
        {used.toLocaleString()}
        <span style={{ fontSize: 13, fontWeight: 400, color: 'var(--fg-muted)' }}>
          {included > 0 ? ` / ${included.toLocaleString()} cr` : ' cr used'}
        </span>
      </div>
      <div style={{ height: 7, background: 'var(--bg-hover)', borderRadius: 999, overflow: 'hidden', margin: '8px 0' }}>
        <div style={{ width: `${pct}%`, height: '100%', background: overage > 0 ? 'var(--danger)' : 'var(--accent)' }} />
      </div>
      {topModels.length > 0 ? (
        topModels.map((m) => (
          <div key={m.modelDeploymentName} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: 'var(--fg-muted)' }}>
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{m.modelDeploymentName}</span>
            <span>{m.creditsConsumed.toLocaleString()} cr</span>
          </div>
        ))
      ) : (
        <div style={{ fontSize: 12, color: 'var(--fg-muted)' }}>No AI usage this period.</div>
      )}
      <div style={{ marginTop: 8, fontSize: 12, color: overage > 0 ? 'var(--danger)' : 'var(--fg-muted)' }}>
        {overage > 0
          ? `${overage.toLocaleString()} credits over the included allotment`
          : headroom !== null
            ? `No overage · ${headroom}% headroom`
            : 'No included allotment set'}
      </div>
    </Card>
  );
}

// ── needs attention ───────────────────────────────────────────────────────────

function AttentionTile({ summary }: { summary: DashboardSummary | null }) {
  const rows = summary?.attention ?? [];
  return (
    <Card style={{ display: 'flex', flexDirection: 'column' }}>
      <CardHeader title="Needs attention" link={{ to: '/runs', label: 'All runs' }} />
      {rows.length === 0 ? (
        <p style={{ color: 'var(--fg-muted)', fontSize: 13, margin: 0 }}>
          {summary ? 'All clear — nothing failed or running.' : 'Loading…'}
        </p>
      ) : (
        <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
          {rows.map((r, i) => (
            <li
              key={r.runId}
              style={{
                display: 'flex', alignItems: 'center', gap: 10, padding: '7px 0',
                borderBottom: i < rows.length - 1 ? '1px solid var(--border)' : 'none', fontSize: 13,
              }}
            >
              <Link
                to={`/runs?workflowId=${r.workflowId}`}
                style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: 'var(--fg)', textDecoration: 'none' }}
              >
                {r.workflowName}
                {r.failingStepName && <span style={{ color: 'var(--fg-muted)' }}> · {r.failingStepName}</span>}
              </Link>
              <span style={{ flex: '0 0 auto' }}>
                <RunStatusBadge status={r.status} />
              </span>
              <span style={{ fontSize: 11, color: 'var(--fg-muted)', width: 40, textAlign: 'right', flex: '0 0 auto' }}>
                {ago(r.completedUtc ?? r.startedUtc)}
              </span>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

// ── live activity — SignalR feed, seeded with recent run history ──────────────

const MAX_FEED = 25;

/** A row in the unified feed — sourced from either a live event or a seeded run. */
interface FeedRow {
  key: string;
  runId: string;
  status: string;
  label: string;
  timeIso: string;
  live: boolean;
}

function liveRow(e: WorkflowActivityEvent, i: number): FeedRow {
  const stepLabel = e.stepDisplayName || e.stepName;
  const label = stepLabel
    ? `${e.workflowName ?? 'Workflow'} · ${stepLabel} → ${e.stepStatus ?? e.runStatus}`
    : `${e.workflowName ?? 'Workflow'} → ${e.runStatus}`;
  return {
    key: `${e.runId}-${e.stepId ?? 'run'}-${e.occurredUtc}-${i}`,
    runId: e.runId,
    status: (e.stepStatus ?? e.runStatus) || '',
    label,
    timeIso: e.occurredUtc,
    live: true,
  };
}

function seedRow(r: WorkflowRunListItem): FeedRow {
  return {
    key: `seed-${r.id}`,
    runId: r.id,
    status: r.status,
    label: `${r.workflowName} → ${statusLabel(r.status)}`,
    timeIso: r.completedUtc ?? r.startedUtc,
    live: false,
  };
}

function LiveActivityTile() {
  const activity = useActivity();
  const [events, setEvents] = useState<WorkflowActivityEvent[]>([]);
  // Recent run history seeds the feed so it shows activity before the next
  // streamed event arrives. null = still loading; [] = loaded, none yet.
  const [seed, setSeed] = useState<WorkflowRunListItem[] | null>(null);

  useEffect(() => {
    if (!activity) return;
    const off = activity.onWorkflowActivity((e) => {
      setEvents((prev) => [e, ...prev].slice(0, MAX_FEED));
    });
    return off;
  }, [activity]);

  useEffect(() => {
    const controller = new AbortController();
    getRecentRuns(MAX_FEED, controller.signal)
      .then((runs) => setSeed(runs))
      .catch((err) => {
        if (err instanceof DOMException && err.name === 'AbortError') return;
        setSeed([]); // a backfill failure must not break the live feed
      });
    return () => controller.abort();
  }, []);

  const connected = activity?.connected ?? false;

  const rows = useMemo(() => {
    const live = events.map(liveRow);
    // Drop seeded runs that the live feed has since superseded, then append the
    // rest below the live events (live = newer than anything loaded at mount).
    const liveRunIds = new Set(live.map((r) => r.runId));
    const seeded = (seed ?? []).filter((r) => !liveRunIds.has(r.id)).map(seedRow);
    return [...live, ...seeded].slice(0, MAX_FEED);
  }, [events, seed]);

  const loading = seed === null && events.length === 0;

  return (
    <Card style={{ display: 'flex', flexDirection: 'column' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
        <h3 style={{ margin: 0, fontSize: 15, fontWeight: 600 }}>Live activity</h3>
        <span
          title={connected ? 'Connected' : 'Reconnecting…'}
          style={{ width: 8, height: 8, borderRadius: 4, background: connected ? 'var(--success)' : 'var(--fg-muted)' }}
        />
      </div>
      {rows.length === 0 ? (
        <p style={{ color: 'var(--fg-muted)', margin: 0, fontSize: 13 }}>
          {loading
            ? 'Loading recent activity…'
            : connected
              ? 'No workflow activity yet.'
              : 'Connecting to the activity feed…'}
        </p>
      ) : (
        <ul style={{ listStyle: 'none', margin: 0, padding: 0, overflowY: 'auto', maxHeight: 220 }}>
          {rows.map((r) => (
            <li
              key={r.key}
              style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 0', fontSize: 13, borderBottom: '1px solid var(--border)', opacity: r.live ? 1 : 0.85 }}
            >
              <span aria-hidden style={{ width: 8, height: 8, borderRadius: 4, background: statusColor(r.status), flex: '0 0 auto' }} />
              <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.label}</span>
              <span style={{ color: 'var(--fg-muted)', fontSize: 11, flex: '0 0 auto' }}>
                {r.live ? formatTime(r.timeIso) : ago(r.timeIso)}
              </span>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

// ── page ──────────────────────────────────────────────────────────────────────

export function Home() {
  const navigate = useNavigate();
  const location = useLocation();
  const [summary, setSummary] = useState<DashboardSummary | null>(null);
  const [usage, setUsage] = useState<CurrentUsage | null>(null);
  const [playbooks, setPlaybooks] = useState<PlaybookSummary[] | null>(null);
  const [pulsarReport, setPulsarReport] = useState<StandingReport | null>(null);
  // The Pulsars section exists only while the region has the feature on — the same
  // switch that draws the Pulsars nav entry (the report endpoint 404s when it's off).
  const { state: auth } = useAuth();
  const pulsarsOn = auth.status === 'authenticated' && auth.user.features?.pulsars === true;
  const [error, setError] = useState<string | null>(null);
  // Bumped by Retry to re-run the dashboard load after a transient failure.
  const [reloadKey, setReloadKey] = useState(0);
  // A one-shot toast handed off via navigation state — e.g. landing here right
  // after a forced first-login password change.
  const [toast, setToast] = useState<string | null>(
    (location.state as { flash?: string } | null)?.flash ?? null,
  );

  // Clear the flash from history state so a reload or back-nav won't re-show it.
  useEffect(() => {
    if ((location.state as { flash?: string } | null)?.flash) {
      navigate('.', { replace: true, state: null });
    }
  }, [location.state, navigate]);

  useEffect(() => {
    let cancelled = false;
    const controller = new AbortController();
    getDashboardSummary(controller.signal)
      .then((s) => { if (!cancelled) { setSummary(s); setError(null); } })
      .catch((err) => {
        if (err instanceof DOMException && err.name === 'AbortError') return;
        if (!cancelled) setError('Could not load the dashboard.');
      });
    // AI usage is best-effort: a failure here must not blank the rest of the page.
    // No AbortSignal on this client method, so guard the state write on unmount.
    getCurrentUsage()
      .then((u) => { if (!cancelled) setUsage(u); })
      .catch(() => { /* leave the usage tile in its empty state */ });
    // Playbooks summary is best-effort too — a failure leaves the strip in its
    // em-dash state without blanking the rest of the dashboard.
    listPlaybooks(controller.signal)
      .then((p) => { if (!cancelled) setPlaybooks(p); })
      .catch((err) => {
        if (err instanceof DOMException && err.name === 'AbortError') return;
        /* leave the playbooks strip in its empty state */
      });
    // Pulsars report: best-effort like the two above.
    if (pulsarsOn) {
      getPulsarReport(controller.signal)
        .then((r) => { if (!cancelled) setPulsarReport(r); })
        .catch((err) => {
          if (err instanceof DOMException && err.name === 'AbortError') return;
          /* leave the Pulsars section in its em-dash state */
        });
    }
    return () => { cancelled = true; controller.abort(); };
    // reloadKey drives Retry: bumping it re-runs the load.
  }, [reloadKey, pulsarsOn]);

  // A brand-new tenant with no runners, no deployments, and no runs gets one
  // guided next-step panel instead of a KPI strip of zeros and four "nothing
  // yet" tiles. Gated on a loaded summary so loading/error still show the tiles.
  const isEmpty =
    !error &&
    summary !== null &&
    summary.runs7d === 0 &&
    summary.activeDeployments === 0 &&
    summary.runnersTotal === 0;
  const reduceMotion = prefersReducedMotion();

  return (
    <div>
      <PageHeader
        title="Dashboard"
        subtitle="Your automation control plane at a glance."
      />

      {error && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            marginBottom: 14,
            fontSize: 13,
          }}
          role="alert"
        >
          <span style={{ color: 'var(--danger)' }}>{error}</span>
          <Button size="small" onClick={() => setReloadKey((k) => k + 1)} data-testid="dashboard-retry">
            Retry
          </Button>
        </div>
      )}

      {isEmpty ? (
        <RunnerOnboarding />
      ) : (
        <>
          {/* Above Playbooks, matching the nav order. */}
          {pulsarsOn && <PulsarsReport report={pulsarReport} />}

          <section aria-label="Playbooks">
            <SectionHeading>Playbooks</SectionHeading>
            <PlaybooksStrip playbooks={playbooks} />
          </section>

          <section aria-label="Workflows">
            <SectionHeading>Workflows</SectionHeading>
            <KpiStrip summary={summary} />
          </section>

          <RunnerTypeCards summary={summary} />

          <section aria-label="Activities">
            <SectionHeading>Activities</SectionHeading>
            <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1.6fr) minmax(0, 1fr)', gap: 12, marginBottom: 12 }}>
              <AttentionTile summary={summary} />
              <LiveActivityTile />
            </div>
          </section>

          <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1.6fr) minmax(0, 1fr)', gap: 12 }}>
            <RunOutcomesTile summary={summary} />
            <AiUsageTile usage={usage} />
          </div>
        </>
      )}

      <NotificationGroup style={{ position: 'fixed', right: 16, bottom: 16, alignItems: 'flex-end' }}>
        {/* Skip the slide/fade when the OS asks for reduced motion — the toast
            still appears, just without the transition. */}
        {reduceMotion ? (
          toast && (
            <Notification type={{ style: 'success', icon: true }} closable onClose={() => setToast(null)}>
              <span>{toast}</span>
            </Notification>
          )
        ) : (
          <Fade>
            {toast && (
              <Notification type={{ style: 'success', icon: true }} closable onClose={() => setToast(null)}>
                <span>{toast}</span>
              </Notification>
            )}
          </Fade>
        )}
      </NotificationGroup>
    </div>
  );
}

export default Home;
